import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Header from "../core/Header";
import Footer from "../core/Footer";
import PageHero from "../components/PageHero";
import { useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabase";
import { useTranslation } from "../utils/i18n";

const TYPE_NORMALIZE = {
  "News": "Nyhet",
  "New": "Nyhet",
  "Article": "Artikel",
  "Information": "Information",
  "Nyhet": "Nyhet",
  "Artikel": "Artikel",
};

const CATEGORIES_CONFIG = [
  { key: "all", dbValue: null },
  { key: "nyhet", dbValue: "Nyhet" },
  { key: "artikel", dbValue: "Artikel" },
  { key: "information", dbValue: "Information" },
];

export default function NewsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState("");
  const [activeCategoryKey, setActiveCategoryKey] = React.useState("all");
  const [search, setSearch] = React.useState("");
  const [visibleCount, setVisibleCount] = React.useState(6);

  React.useEffect(() => {
    let isMounted = true;

    const fetchNewsletters = async () => {
      setIsLoading(true);
      setFetchError("");

      const { data, error } = await supabase
        .from("newsLetter")
        .select("id, created_at, title, description, type, image_url")
        .order("created_at", { ascending: false });

      if (!isMounted) return;

      if (error) {
        setFetchError(error.message || "Kunde inte hämta nyhetsbrev.");
        setItems([]);
      } else {
        setItems(Array.isArray(data) ? data : []);
      }

      setIsLoading(false);
    };

    fetchNewsletters();
    return () => { isMounted = false; };
  }, []);

  const mappedItems = items.map((item) => ({
    id: item.id,
    tag: TYPE_NORMALIZE[item.type] || item.type || "Nyhet",
    title: item.title || "",
    excerpt: item.description || "",
    imageUrl: item.image_url || "",
    date: item.created_at
      ? new Date(item.created_at).toLocaleDateString("sv-SE", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "",
  }));

  const activeCategory = CATEGORIES_CONFIG.find((c) => c.key === activeCategoryKey);

  const categoryLabel = (tag) => {
    const cat = CATEGORIES_CONFIG.find((c) => c.dbValue === tag);
    return cat ? t(`newsPage.categories.${cat.key}`) : tag;
  };

  const filtered = mappedItems.filter((item) => {
    const matchesCategory =
      activeCategory.dbValue === null || item.tag === activeCategory.dbValue;
    const matchesSearch =
      !search ||
      item.title.toLowerCase().includes(search.toLowerCase()) ||
      item.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featured = filtered[0] || null;
  const rest = filtered.slice(1, visibleCount + 1);
  const hasMore = filtered.length > visibleCount + 1;

  const goTo = (id) => navigate(`/news/${id}`);

  return (
    <Box sx={{ width: "100%", backgroundColor: "#fff", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />

      <PageHero
        overline={t("newsPage.overline")}
        title={t("newsPage.title")}
        subtitle={t("newsPage.subtitle")}
      />

      {/* Filter bar */}
      <Box
        sx={{
          borderBottom: "1px solid #E3E6EB",
          bgcolor: "#fff",
          position: "sticky",
          top: { xs: 56, md: 64 },
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            maxWidth: 1440,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 5 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {CATEGORIES_CONFIG.map((cat) => (
              <Chip
                key={cat.key}
                label={t(`newsPage.categories.${cat.key}`)}
                onClick={() => {
                  setActiveCategoryKey(cat.key);
                  setVisibleCount(6);
                }}
                sx={{
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  cursor: "pointer",
                  bgcolor: activeCategoryKey === cat.key ? "#FF6A00" : "transparent",
                  color: activeCategoryKey === cat.key ? "#fff" : "#475467",
                  border: "1px solid",
                  borderColor: activeCategoryKey === cat.key ? "#FF6A00" : "#E3E6EB",
                  "&:hover": {
                    bgcolor: activeCategoryKey === cat.key ? "#E05500" : "#F6F7F9",
                  },
                  "& .MuiChip-label": { px: 1.5, py: 0.5 },
                }}
              />
            ))}
          </Box>

          <OutlinedInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("newsPage.searchPlaceholder")}
            size="small"
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#9AA4B2" }} />
              </InputAdornment>
            }
            sx={{
              borderRadius: 999,
              fontSize: "0.875rem",
              width: 200,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E3E6EB" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
            }}
          />
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 4, md: 6 },
        }}
      >
        {fetchError && (
          <Typography color="error" sx={{ mb: 3 }}>
            {fetchError}
          </Typography>
        )}

        {isLoading && (
          <Typography color="text.secondary">{t("newsPage.loading")}</Typography>
        )}

        {!isLoading && filtered.length === 0 && (
          <Box
            sx={{
              border: "1px dashed #E3E6EB",
              borderRadius: 3,
              p: 5,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {t("newsPage.noResults")}
            </Typography>
            <Typography color="text.secondary">
              {t("newsPage.noResultsHint")}
            </Typography>
          </Box>
        )}

        {/* Featured article */}
        {!isLoading && featured && (
          <Box
            onClick={() => goTo(featured.id)}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #E3E6EB",
              cursor: "pointer",
              mb: { xs: 4, md: 5 },
              transition: "box-shadow 180ms ease",
              "&:hover": {
                boxShadow: "0 16px 40px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Box
              sx={{
                minHeight: { xs: 220, md: 340 },
                bgcolor: "#E8EEF5",
                backgroundImage: featured.imageUrl
                  ? `url(${featured.imageUrl})`
                  : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />

            <Box
              sx={{
                p: { xs: 3, md: 4 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                bgcolor: "#fff",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Chip
                  label={categoryLabel(featured.tag)}
                  size="small"
                  sx={{
                    bgcolor: "#F6F7F9",
                    color: "#475467",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    borderRadius: 999,
                  }}
                />
                <Typography variant="caption" sx={{ color: "#9AA4B2" }}>
                  {featured.date}
                </Typography>
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.2,
                  color: "#0B0F13",
                  fontSize: { xs: "1.6rem", md: "2rem" },
                  mb: 2,
                }}
              >
                {featured.title}
              </Typography>

              <Typography
                sx={{
                  color: "#475467",
                  lineHeight: 1.7,
                  mb: 3,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {featured.excerpt}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: "#FF6A00",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                }}
              >
                {t("newsPage.readFull")}
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Box>
            </Box>
          </Box>
        )}

        {/* Article grid */}
        {!isLoading && rest.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 3,
              mb: { xs: 4, md: 6 },
            }}
          >
            {rest.map((n) => (
              <Box
                key={n.id}
                onClick={() => goTo(n.id)}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #E3E6EB",
                  overflow: "hidden",
                  bgcolor: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 180ms ease, box-shadow 180ms ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 16px 36px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <Box
                    sx={{
                      height: 200,
                      bgcolor: "#E8EEF5",
                      backgroundImage: n.imageUrl ? `url(${n.imageUrl})` : "none",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      left: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={categoryLabel(n.tag)}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.9)",
                        color: "#1D2129",
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        borderRadius: 999,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255,255,255,0.9)",
                        fontWeight: 500,
                        textShadow: "0 1px 3px rgba(0,0,0,0.4)",
                      }}
                    >
                      {n.date}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#0B0F13",
                      lineHeight: 1.35,
                      mb: 1,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {n.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#475467",
                      lineHeight: 1.6,
                      flex: 1,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {n.excerpt}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 2.5,
                      color: "#FF6A00",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                    }}
                  >
                    {t("newsPage.readMore")}
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Load more */}
        {!isLoading && hasMore && (
          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="contained"
              onClick={() => setVisibleCount((v) => v + 6)}
              sx={{
                bgcolor: "#FF6A00",
                color: "#fff",
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                px: 4,
                py: 1.25,
                boxShadow: "none",
                "&:hover": { bgcolor: "#E05500", boxShadow: "none" },
              }}
            >
              {t("newsPage.loadMore")}
            </Button>
          </Box>
        )}
      </Box>

      <Footer />
    </Box>
  );
}
